/**
 * Admin Service
 * Handles admin-only operations (requires super_admin role)
 */

import apiClient from "./api";

class AdminService {
  /**
   * Create new user (add to whitelist)
   * @param {number} telegramId - Telegram user ID
   * @param {string} role - User role ('admin' or 'manager')
   * @returns {Promise<Object>} Created user data
   */
  async createUser(telegramId, role) {
    const response = await apiClient.post("/admin/users", {
      telegram_id: telegramId,
      role,
    });
    
    if (!response.success) {
      throw new Error(response.message || "Failed to create user");
    }
    
    return response.data;
  }

  /**
   * Promote user to admin or manager
   * @param {number} telegramId - Telegram user ID
   * @param {string} role - New role ('admin' or 'manager')
   * @returns {Promise<Object>} Updated user data
   */
  async promoteUser(telegramId, role) {
    const response = await apiClient.post("/admin/users/promote", {
      telegram_id: telegramId,
      role,
    });
    
    if (!response.success) {
      throw new Error(response.message || "Failed to promote user");
    }
    
    return response.data;
  }

  /**
   * Demote user to manager
   * @param {number} telegramId - Telegram user ID
   * @returns {Promise<Object>} Updated user data
   */
  async demoteUser(telegramId) {
    const response = await apiClient.post("/admin/users/demote", {
      telegram_id: telegramId,
    });
    
    if (!response.success) {
      throw new Error(response.message || "Failed to demote user");
    }
    
    return response.data;
  }

  /**
   * Get list of all users
   * @param {string} role - Filter by role (optional)
   * @returns {Promise<Array>} Array of users with their stats
   */
  async getUsers(role = null) {
    const params = role ? { role } : {};
    const response = await apiClient.get("/admin/users/list", params);
    
    if (!response.success) {
      throw new Error(response.message || "Failed to fetch users");
    }
    
    return response.data.users || [];
  }

  /**
   * Delete user (remove from whitelist)
   * @param {number} telegramId - Telegram user ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteUser(telegramId) {
    const response = await apiClient.delete(`/admin/users/${telegramId}`);
    
    if (!response.success) {
      throw new Error(response.message || "Failed to delete user");
    }
    
    return response.data;
  }

  /**
   * Get list of all managers and admins for filtering
   * @returns {Promise<Array>} Array of managers with basic info
   */
  async getManagers() {
    const response = await apiClient.get("/admin/users/managers");
    
    if (!response.success) {
      throw new Error(response.message || "Failed to fetch managers");
    }
    
    return response.data || [];
  }

  /**
   * Get list of all events
   * @returns {Promise<Array>} Array of events
   */
  async getEvents() {
    const response = await apiClient.get("/admin/events/list");
    
    if (!response.success) {
      throw new Error(response.message || "Failed to fetch events");
    }
    
    return response.data || [];
  }

  /**
   * Create new event
   * @param {Object} eventData - Event data {code, name, is_default}
   * @returns {Promise<Object>} Created event
   */
  async createEvent(eventData) {
    const response = await apiClient.post("/admin/events", eventData);
    
    if (!response.success) {
      throw new Error(response.message || "Failed to create event");
    }
    
    return response.data;
  }

  /**
   * Update event
   * @param {number} eventId - Event ID
   * @param {Object} updates - Fields to update {name, is_active, is_default}
   * @returns {Promise<Object>} Updated event
   */
  async updateEvent(eventId, updates) {
    const response = await apiClient.patch(`/admin/events/${eventId}`, updates);
    
    if (!response.success) {
      throw new Error(response.message || "Failed to update event");
    }
    
    return response.data;
  }

  /**
   * Delete event
   * @param {number} eventId - Event ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteEvent(eventId) {
    const response = await apiClient.delete(`/admin/events/${eventId}`);
    
    if (!response.success) {
      throw new Error(response.message || "Failed to delete event");
    }
    
    return response.data;
  }
}

export const adminService = new AdminService();
export default adminService;
